import { AwsRum } from 'aws-rum-web';
import pkg from '../package.json';

const APPLICATION_VERSION = pkg.version;

const { REACT_APP_TDTK_ENV, REACT_APP_RUM_APP_ID, REACT_APP_RUM_IDENTITY_POOL_ID } = import.meta.env;

// Configuration: https://github.com/aws-observability/aws-rum-web/blob/main/docs/configuration.md
// APP_ID and IDENTITY_POOL_ID are injected per-environment by the deploy pipeline; both are
// public client identifiers (the JS bundle ships to the browser), so they're not secrets.
const APPLICATION_ID = REACT_APP_RUM_APP_ID;
const APPLICATION_REGION = 'us-east-1';
const IDENTITY_POOL_ID = REACT_APP_RUM_IDENTITY_POOL_ID;

const IS_NON_PROD = REACT_APP_TDTK_ENV !== 'prod' && REACT_APP_TDTK_ENV !== 'preprod';
const RUM_CONFIGURED = !!(APPLICATION_ID && IDENTITY_POOL_ID);
const IS_UAT = REACT_APP_TDTK_ENV === 'uat';
const RUM_ENABLED = RUM_CONFIGURED && (!IS_NON_PROD || IS_UAT); // only enable RUM for  uat for non-prod
// Verbose `[RUM] …` console logging is non-prod only; prod/preprod stays quiet but still ships events.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rumDebug = (...args: any[]) => {
  if (IS_NON_PROD) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};

let awsRum: AwsRum | undefined;

try {
  if (RUM_ENABLED) {
    // errors telemetry is required for recordError() — it loads JsErrorPlugin which recordError() delegates to.
    // performance and http are owned by the HTML CDN snippet to avoid duplicate recording.
    awsRum = new AwsRum(APPLICATION_ID!, APPLICATION_VERSION, APPLICATION_REGION, {
      allowCookies: true,
      endpoint: `https://dataplane.rum.${APPLICATION_REGION}.amazonaws.com`,
      identityPoolId: IDENTITY_POOL_ID!,
      sessionSampleRate: 1,
      sessionEventLimit: 0,
      telemetries: ['errors'],
    });
    rumDebug('[RUM] initialized', { env: REACT_APP_TDTK_ENV, appId: APPLICATION_ID });
  } else if (IS_NON_PROD) {
    // eslint-disable-next-line no-console
    console.warn('[RUM] skipped: REACT_APP_RUM_APP_ID or REACT_APP_RUM_IDENTITY_POOL_ID not set');
  }
} catch (e) {
  // eslint-disable-next-line no-console
  if (IS_NON_PROD) console.warn('[RUM] init failed', e);
}

export { awsRum };

export type RumSessionAttrs = {
  userId?: string;
  userRole?: string;
  siteId?: string;
  eventId?: string;
  asmtEventTypeCd?: string;
  testCenterCd?: string;
};

export type RumErrorAttrs = RumSessionAttrs & {
  errorCode?: string;
  source?: string;
  query?: string;
  correlationId?: string;
};

const sanitize = (attrs: Record<string, unknown>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(attrs)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => [k, String(v)])
  );

/**
 * Set after login resolves so user/org context is attached to every RUM event in the session.
 */
export const setRumUserSession = (attrs: RumSessionAttrs): void => {
  const sanitized = sanitize(attrs);
  rumDebug('[RUM] setRumUserSession', { active: !!awsRum, attrs: sanitized });
  awsRum?.addSessionAttributes(sanitized);
};

/**
 * Record an error to RUM and attach optional context as session attributes. Preserves the original Error object
 * when one is supplied so the RUM stack trace remains useful.
 *
 * NOTE: `addSessionAttributes` is session-scoped, so these attributes will apply to subsequent events in the
 * session until they are overwritten. AWS RUM caps custom session attributes at 10. We push 6 user/org attrs at
 * login and add 4 error-context attrs (errorCode, source, query, correlationId) — total 10. Do not add more
 * without dropping one, or new attributes will be silently rejected by the SDK.
 */
export const recordRumError = (error: unknown, attrs: RumErrorAttrs = {}): void => {
  if (!awsRum) {
    rumDebug('[RUM] recordRumError skipped (awsRum not initialized)', { attrs });
    return;
  }
  const sanitized = sanitize(attrs);
  if (Object.keys(sanitized).length > 0) {
    awsRum.addSessionAttributes(sanitized);
  }
  const stringifyError = (value: unknown): string => {
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  };
  const err = error instanceof Error ? error : new Error(stringifyError(error));
  rumDebug('[RUM] recordRumError', {
    correlationId: sanitized.correlationId,
    source: sanitized.source,
    errorCode: sanitized.errorCode,
    message: err.message,
    attrs: sanitized,
  });
  try {
    awsRum.recordError(err);
    // dispatchBeacon uses sendBeacon — guaranteed delivery even if the user navigates away,
    // unlike dispatch() which uses fetch/XHR and can be cancelled on page unload.
    awsRum.dispatchBeacon();
  } catch (e) {
    if (IS_NON_PROD) console.warn('[RUM] recordError/dispatchBeacon failed', e); // eslint-disable-line no-console
  }
};
